<template>
  <footer
    id="site-footer"
    ref="footerContainer"
    :class="footerClass">
    <div
      ref="footerRef"
      class="gutter-wrapper">
      <div class="footer-container">
        <div class="footer-head">
          <router-link
            :to="'/'"
            class="footer-logo"
            title="Home">
            <FooterIcon />
          </router-link>
          <div class="social-media">
            <div class="menus-container">
              <div class="footer-col-1">
                <FooterMenu name="footer-menu" />
              </div>
            </div>

            <div class="social-container">
              <div class="social-media-container">
                <a
                  v-if="acf_options?.linkedin"
                  :href="acf_options?.linkedin"
                  target="_blank">
                  Linkedin
                </a>

                <a
                  v-if="acf_options?.x"
                  :href="acf_options?.x"
                  target="_blank">
                  X
                </a>
              </div>

              <div class="footer-address-container">
                <div
                  class="address"
                  v-html="acf_options?.address"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="footer-body">
          <p class="rights">
            Capstan Therapeutics, Inc. © {{ currentYear }} <br />
            <span class="block mt-3 rights">Designed and Developed by
              <a
                href="https://www.struck.com"
                target="_blank"
                class="underline no-anim cursor-pointer"
                >Struck</a >
              </span>
          </p>

          <div class="legal-container">
            <template
              v-for="(link, indx) of acf_options?.footer_links"
              :key="`footer-link-${indx}`">
              <a
                :href="link.link.url"
                :target="link.link.target"
                class="mr-1"
                >{{ link.link.title }}</a
              >
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Bottom Logo -->
    <div
      ref="footerLogo"
      class="footer-bottom-logo">
      <FooterLogo />
    </div>
    <!-- end of Footer Bottom Logo -->
  </footer>
</template>

<script setup>
import FooterMenu from '@/components/template-parts/FooterMenu.vue'
import FooterLogo from '@/components/template-parts/FooterLogo.vue'
import FooterIcon from '@/components/template-parts/FooterIcon.vue'
import { useStore } from '@/stores/main'
import { ref, computed, onBeforeMount, watch, onMounted } from 'vue'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import useSiteLoading from '@/composables/useSiteLoading'
import useUtils from '@/composables/useUtils'

const { siteLoading } = useSiteLoading()

gsap.registerPlugin(ScrollTrigger)

const { genUUID, device } = useUtils()
const store = useStore()
const acf_options = ref(null)
const header_options = ref(null)
const footerLogo = ref(null)
const footerContainer = ref(null)
const footerRef = ref(null)
const footerLogoId = genUUID()
const footerRefId = genUUID()
const footerClass = ref('site-footer')

const animateContent = () => {
  if (device.value !== 'desktop') return
  gsap.fromTo(
    footerRef.value,
    { y: '-100%' },
    {
      y: '0',
      duration: 5,
      scrollTrigger: {
        trigger: footerContainer.value,
        id: footerRefId,
        start: 'top 100%',
        end: 'bottom bottom',
        endTrigger: footerContainer.value,
        scrub: true,
        markers: false,
      },
    }
  )
}

watch(device, () => {
  if (device.value === 'desktop') {
    animateContent()
  } else {
    ScrollTrigger.getById(footerRefId)?.kill()
    gsap.set(footerRef.value, { clearProps: true })
  }
})

const animateLogo = () => {
  gsap.fromTo(
    footerLogo.value,
    { y: '100%' },
    {
      y: '0',
      scrollTrigger: {
        trigger: footerLogo.value,
        id: footerLogoId,
        start: 'top-=100% bottom',
        end: 'bottom bottom-=10%',
        scrub: true,
        markers: false,
      },
    }
  )
}

const currentYear = computed(() => {
  return new Date().getFullYear()
})

onMounted(() => {
  animateLogo()
  animateContent()
})

watch(siteLoading, () => {
  setTimeout(() => {
    ScrollTrigger.refresh()
  }, 500)
})

onBeforeMount(async () => {
  ScrollTrigger.getById(footerRefId)?.kill()
  ScrollTrigger.getById(footerLogoId)?.kill()
  acf_options.value = await store.acfOptions()
  header_options.value = await store.headerOptions()
})
</script>

<style lang="scss">
.site-footer {
  @apply w-full z-20 -mb-16 pb-16 mx-auto bg-capstan-dark text-capstan-lavender px-5 pt-9 relative overflow-hidden 
  flex flex-col justify-between xl:min-h-screen;
  @apply transition-colors duration-500 ease-in-out;

  .gutter-wrapper {
    a {
      @apply relative;

      &:before {
        @apply content-[''] top-auto bottom-0 absolute bg-capstan-lavender transition-width h-[1px] left-0 w-0 block;
      }

      &:hover {
        &:before {
          @apply w-full;
        }
      }
    }
  }

  .footer-icon {
    &.purple {
      @apply block;
    }

    &.yellow {
      @apply hidden;
    }

    &.green {
      @apply hidden;
    }

    &.gray {
      @apply hidden;
    }
  }

  .footer-container {
    @apply divide-text;
  }

  .footer-head {
    @apply flex flex-col md:flex-row md:flex-wrap md:justify-between md:pt-10 md:pb-8 lg:pb-20;

    .footer-logo {
      @apply h-fit max-w-[52px] relative -top-1;

      &:hover {
        &:before,
        &:after {
          @apply hidden;
        }
      }
    }

    .social-media {
      @apply flex flex-col items-start py-4 md:w-full md:flex-row md:gap-16
    lg:w-[60%] lg:mr-0 lg:gap-20 xl:gap-48;

      .menus-container {
        @apply flex flex-col my-9 w-full lg:my-0;
      }

      .social-container {
        @apply flex flex-col gap-3 pt-6 relative w-full md:w-[40%] md:flex-own md:mr-0 md:pt-10
      lg:pt-1;

        * {
          @apply text-capstan-lavender body-copy-2;
        }

        .footer-address-container {
          @apply mb-0 order-1;
        }

        .social-media-container {
          @apply order-2 mb-0 flex flex-col gap-3;

          a {
            @apply mb-0 w-fit;
          }
        }
      }
    }
  }

  .footer-body {
    @apply flex flex-col text-[11px] pt-4 pb-0 md:flex-row md:justify-between lg:w-[60%] lg:ml-auto lg:mr-0 lg:gap-20 xl:gap-48;

    * {
      @apply body-copy-2 text-capstan-lavender;
    }

    .rights {
      @apply order-2 md:order-1;
    }

    .legal-container {
      @apply flex flex-col gap-3 order-1 md:order-2 md:w-[40%] md:mr-0;

      a {
        @apply w-fit mb-0 body-copy-2;
      }
    }
  }

  .footer-bottom-logo {
    @apply w-full max-w-full py-6 lg:pb-0 lg:mt-28;
  }

  // Sky theme
  &.sky {
    @apply bg-capstan-sky;

    li {
      @apply border-b-capstan-dark;

      a {
        &:after {
          @apply bg-capstan-dark;
        }

        &.router-link-exact-active {
          &:after {
            @apply bg-capstan-dark;
          }
        }
      }
    }

    a {
      @apply text-capstan-dark;

      &:before {
        @apply bg-capstan-dark;
      }
    }

    .footer-icon {
      &.purple {
        @apply hidden;
      }

      &.yellow {
        @apply hidden;
      }

      &.green {
        @apply block;
      }

      &.gray {
        @apply hidden;
      }
    }

    .footer-icon {
      &.yellow {
        @apply hidden;
      }

      &.green {
        @apply block;
      }

      &.gray {
        @apply hidden;
      }
    }

    .footer-head {
      .social-media {
        .social-container {
          * {
            @apply text-capstan-dark;
          }
        }
      }
    }

    .footer-body {
      .rights {
        @apply text-capstan-dark;
      }
    }

    .footer-bottom-logo {
      svg {
        path {
          @apply fill-capstan-dark;
        }
      }
    }
  }

  // Yellow theme
  &.yellow {
    @apply bg-capstan-yellow;

    li {
      @apply border-b-capstan-dark;

      a {
        &:after {
          @apply bg-capstan-dark;
        }

        &.router-link-exact-active {
          &:after {
            @apply bg-capstan-dark;
          }
        }
      }
    }

    a {
      @apply text-capstan-dark;

      &:before {
        @apply bg-capstan-dark;
      }
    }

    .footer-icon {
      &.purple {
        @apply hidden;
      }

      &.yellow {
        @apply hidden;
      }

      &.green {
        @apply block;
      }

      &.gray {
        @apply hidden;
      }
    }

    .footer-head {
      .footer-icon {
        path,
        defs,
        linearGradient,
        stop {
          @apply text-capstan-dark w-[52px];
        }
      }

      .social-media {
        .social-container {
          * {
            @apply text-capstan-dark;
          }
        }
      }
    }

    .footer-body {
      .rights {
        @apply text-capstan-dark;
      }
    }

    .footer-bottom-logo {
      svg {
        path {
          @apply fill-capstan-dark;
        }
      }
    }
  }

  // Purple theme
  &.purple {
    @apply bg-capstan-purple;

    li {
      @apply border-b-capstan-warm;

      a {
        &:after {
          @apply bg-capstan-warm;
        }

        &.router-link-exact-active {
          &:after {
            @apply bg-capstan-warm;
          }
        }
      }
    }

    a {
      @apply text-capstan-warm;

      &:before {
        @apply bg-capstan-warm;
      }
    }

    .footer-icon {
      &.purple {
        @apply hidden;
      }

      &.yellow {
        @apply hidden;
      }

      &.green {
        @apply hidden;
      }

      &.gray {
        @apply block;
      }
    }

    .footer-head {
      .footer-icon {
        path,
        defs,
        linearGradient,
        stop {
          @apply text-capstan-warm;
        }
      }

      .social-media {
        .social-container {
          * {
            @apply text-capstan-warm;
          }
        }
      }
    }

    .footer-body {
      .rights {
        @apply text-capstan-warm;
      }
    }

    .footer-bottom-logo {
      svg {
        path {
          @apply fill-capstan-warm;
        }
      }
    }
  }

  // Green theme
  &.green {
    @apply bg-capstan-green;

    li {
      @apply border-b-capstan-warm;

      a {
        &:after {
          @apply bg-capstan-warm;
        }

        &.router-link-exact-active {
          &:after {
            @apply bg-capstan-warm;
          }
        }
      }
    }

    a {
      @apply text-capstan-warm;

      &:before {
        @apply bg-capstan-warm;
      }
    }

    .footer-icon {
      &.purple {
        @apply hidden;
      }

      &.yellow {
        @apply hidden;
      }

      &.green {
        @apply hidden;
      }

      &.gray {
        @apply block;
      }
    }

    .footer-head {
      .footer-icon {
        path,
        defs,
        linearGradient,
        stop {
          @apply text-capstan-warm;
        }
      }

      .social-media {
        .social-container {
          * {
            @apply text-capstan-warm;
          }
        }
      }
    }

    .footer-body {
      .rights {
        @apply text-capstan-warm;
      }
    }

    .footer-bottom-logo {
      svg {
        path {
          @apply fill-capstan-warm;
        }
      }
    }
  }
}
</style>
